import React, { FC, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useVideoFeedback } from "./useVideoFeedback";
import { Button } from "../../../../components/Button/Button";
import { IconCircledCheck } from "../../../../utils/icons";
import {
    ConfirmationProps,
    ApproveDialogProps,
    RequestEditDialogProps,
    PREVIOUS_VERSION_MESSAGE,
    PREVIOUS_VERSION_TITLE,
    REVIEW_LOCKED_ERROR,
    REVIEW_LOCKED_MESSAGE,
    REVIEW_LOCKED_TITLE
} from "./constants";
import { PostSuccessDialog, PostSuccessDialogProps } from "../PostSuccessDialog";
import { ConfirmationDialog } from "../../../../components/Modal/ConfirmationDialog";
import { FeedbackCriteria } from "./FeedbackCriteria";
import { BRAND_PROJECT_STATUSES } from "../../helper";
import { formatVideoName } from "../../../admin/videos/hooks/useFetchVideos";
import { useUser } from "../../../../hooks/useUser";
import { canApproveUnapproveVideos } from "../../../../utils";
import { DownloadBtn } from "../../../admin/videos/selectedVideoCardV2/DownloadBtn";
import { fileToIVideo } from "../../../../utils/downloadHelpers";
import { apiErrorCode } from "../../../../utils/apiError";

export const FeedbackHeader: FC = () => {
    const {
        onApprove,
        project,
        video,
        onRequestEdit,
        onApproved,
        isPublicPage,
        isReviewLocked,
        readOnly,
        downloadFileTypes
    } = useVideoFeedback();
    const { role } = useUser();
    const navigate = useNavigate();

    const [confirmationData, setConfirmationData] = useState<ConfirmationProps>();
    const [showSuccessPopup, setShowPopup] = useState<PostSuccessDialogProps>();
    // The dialog stays open until its request settles, so a second click on
    // Confirm would send it again; the second answer (409, already submitted)
    // then replaced the first one's success popup with an error.
    const confirming = useRef(false);

    // The version under review, in the shape DownloadBtn expects. Public pages
    // adapt their files with `final: approved` and location = downloadUrl;
    // logged-in callers pass the raw project file (bucket/key/final present).
    const downloadableVideo = useMemo(
        () => (video?.id && (video.location || video.key) ? fileToIVideo(video) : null),
        [video]
    );

    const handleApprove = async () => {
        setConfirmationData({
            ...ApproveDialogProps,
            onConfirm: async () => {
                try {
                    if (onApprove) {
                        await onApprove();
                    }

                    toast.success("Video approved successfully!");

                    await new Promise((resolve) => setTimeout(resolve, 500));

                    if (onApproved) {
                        onApproved();
                    }

                    if (isPublicPage) {
                        setShowPopup({
                            title: "Approved",
                            icon: <IconCircledCheck />,
                            text: "Your video has been approved. Thank you!",
                            onClose: () => setShowPopup(undefined)
                        });
                    } else {
                        setShowPopup({
                            title: "Approved",
                            icon: <IconCircledCheck />,
                            text: 'Your video is now available to view and download in your "Videos" tab.',
                            actions: [
                                <Box className="w-full flex items-center justify-center">
                                    <Button
                                        onClick={() => {
                                            setShowPopup(undefined);
                                            navigate("/video-production/projects");
                                        }}
                                    >
                                        Go to Projects
                                    </Button>
                                </Box>
                            ],
                            onClose: () => {
                                setShowPopup(undefined);
                                navigate("/video-production/projects");
                            }
                        });
                    }
                } catch (error) {
                    toast.error("Failed to approve video. Please try again.");
                    setShowPopup({
                        title: "Error",
                        icon: <IconCircledCheck />,
                        text: "Failed to approve video. Please try again.",
                        actions: [
                            <Box className="w-full flex items-center justify-center">
                                <Button onClick={() => setShowPopup(undefined)}>Close</Button>
                            </Box>
                        ],
                        onClose: () => setShowPopup(undefined)
                    });
                }
            }
        });
    };

    const handleRequestEdit = async () => {
        setConfirmationData({
            ...RequestEditDialogProps,
            onConfirm: async () => {
                try {
                    if (onRequestEdit) {
                        await onRequestEdit();
                    }
                } catch (error) {
                    // onRequestEdit rejects with the bare axios error ("Request
                    // failed with status code 409") and nothing downstream
                    // caught it: ConfirmationDialog wires onConfirm to a plain
                    // onClick, so the rejection escaped as an unhandled
                    // rejection — reported to PostHog, with the guest left
                    // looking at an open dialog and no message at all. 409 is
                    // the API's FeedbackAlreadySubmitted; the button is hidden
                    // on isReviewLocked, but that is derived from the status
                    // this page loaded with, so a second tab, a double-click or
                    // a status change after load all still reach here.
                    const alreadySubmitted = apiErrorCode(error) === "FeedbackAlreadySubmitted";
                    const message = alreadySubmitted
                        ? REVIEW_LOCKED_ERROR
                        : "Failed to submit feedback. Please try again.";
                    // The share page refetches, so it shows the locked review
                    // instead of a Submit Feedback button that 409s again.
                    if (alreadySubmitted && isPublicPage) onApproved?.();
                    toast.error(message);
                    setShowPopup({
                        title: "Error",
                        icon: <IconCircledCheck />,
                        text: message,
                        actions: [
                            <Box className="w-full flex items-center justify-center">
                                <Button onClick={() => setShowPopup(undefined)}>Close</Button>
                            </Box>
                        ],
                        onClose: () => setShowPopup(undefined)
                    });
                    return;
                }
                if (isPublicPage) {
                    setShowPopup({
                        title: "Feedback Submitted",
                        icon: <IconCircledCheck />,
                        text: "Your edit requests have been shared with our team.",
                        onClose: () => setShowPopup(undefined)
                    });
                } else {
                    setShowPopup({
                        title: "Success",
                        icon: <IconCircledCheck />,
                        text: "Your edit requests have been shared.",
                        actions: [
                            <Box className="w-full flex items-center justify-center">
                                <Button onClick={() => navigate("/video-production/projects")}>Go to Projects</Button>
                            </Box>
                        ],
                        onClose: () => setShowPopup(undefined)
                    });
                }
            }
        });
    };

    const renderedBtn = useMemo(() => {
        const canApprove = isPublicPage || canApproveUnapproveVideos(role || "");

        if (!canApprove) {
            return null;
        }

        const shouldDisableBtn = readOnly || !!video?.final;
        const isApproveDisabled = shouldDisableBtn || !canApprove;

        const className = classNames("!flex items-center !gap-2 !px-5 !py-2 !whitespace-nowrap", {
            "!opacity-50 pointer-events-none": shouldDisableBtn
        });

        return (
            <>
                {/* Feedback is already in (project is Editing): nothing to
                    re-submit, and the API would 409 for non-internal users. */}
                {(readOnly || !isReviewLocked) && (
                    <Button
                        onClick={handleRequestEdit}
                        className={classNames(
                            className,
                            "cursor-pointer transition-colors hover:!bg-tint hover:!border-accent"
                        )}
                        color="primary"
                        variant="outlined"
                        disabled={shouldDisableBtn}
                    >
                        Submit Feedback
                    </Button>
                )}
                <Button
                    onClick={handleApprove}
                    className={className}
                    color="primary"
                    variant="contained"
                    disabled={isApproveDisabled}
                    sx={{
                        "&.Mui-disabled": {
                            opacity: 0.3
                        }
                    }}
                >
                    Approve Video
                </Button>
            </>
        );
    }, [onApprove, role, video?.final, isReviewLocked, readOnly, handleRequestEdit, handleApprove]);

    return (
        <Box className="w-full flex flex-col">
            <Box className="w-full flex items-center pb-4 justify-between">
                <Box className="w-2/3 inline-flex items-center gap-x-3 flex-wrap">
                    <Typography variant="h5">
                        {formatVideoName(video?.name || video?.key || video?.title || "")} (Version {video?.version})
                    </Typography>
                </Box>
                <Box className="flex gap-4 items-center">
                    {/* Reviewers may pull the cut they are reviewing (also once
                        the project is back in Editing). DownloadBtn keeps the
                        pre-approval rule: external viewers get the watermarked
                        file only unless the brand allows pre-approval downloads. */}
                    {downloadableVideo && (
                        <DownloadBtn
                            video={downloadableVideo}
                            project={project}
                            allowUnapproved
                            disabled={readOnly}
                            allowedFileTypes={downloadFileTypes}
                        />
                    )}
                    {renderedBtn}
                </Box>
            </Box>
            <Box className="flex flex-col gap-4 items-left w-2/3">
                {isReviewLocked ? (
                    <Box
                        className="flex flex-col gap-1 p-4 rounded-xl border border-border-token"
                        sx={{ backgroundColor: "var(--surface-2)" }}
                    >
                        <Typography variant="body2" fontWeight={600} color="textPrimary">
                            {readOnly ? PREVIOUS_VERSION_TITLE : REVIEW_LOCKED_TITLE}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {readOnly ? PREVIOUS_VERSION_MESSAGE : REVIEW_LOCKED_MESSAGE}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box>
                            We hope you enjoy the video! Please use the feedback form below to share your
                            impressions—tell us what resonated, what could be improved, or any ideas you have. Your
                            comments help us create better content, so don’t hesitate to let us know how the video
                            landed for you.
                        </Box>
                        <FeedbackCriteria />
                    </>
                )}
            </Box>
            {showSuccessPopup && <PostSuccessDialog {...showSuccessPopup} />}
            {confirmationData && (
                <ConfirmationDialog
                    show={Boolean(confirmationData)}
                    {...confirmationData}
                    onCancel={() => setConfirmationData(undefined)}
                    onConfirm={async () => {
                        // ConfirmationDialog hands this straight to a Button
                        // onClick, so anything escaping here is an unhandled
                        // rejection. Each onConfirm owns its own user-facing
                        // copy; this only guarantees the dialog closes.
                        if (confirming.current) return;
                        confirming.current = true;
                        try {
                            await confirmationData?.onConfirm();
                        } catch {
                            // already surfaced inside onConfirm
                        } finally {
                            confirming.current = false;
                            setConfirmationData(undefined);
                        }
                    }}
                />
            )}
        </Box>
    );
};
